import { GameLoopEnvironmentConfig } from './GameLoopEnvironmentConfig';

interface TokenResponseBody {
    readonly token?: unknown;
}

interface JwtPayload {
    readonly exp?: unknown;
}

const DEFAULT_SKEW_SECONDS = 60;

export class GameLoopJwtTokenService {
    private _token: string | null;
    private _pendingFetch: Promise<string> | null;
    private _boundUserId: string | null;
    private readonly _envConfig: GameLoopEnvironmentConfig;
    private readonly _skewSeconds: number;

    constructor(envConfig: GameLoopEnvironmentConfig, skewSeconds: number = DEFAULT_SKEW_SECONDS) {
        this._envConfig = envConfig;
        this._skewSeconds = skewSeconds;
        this._token = null;
        this._pendingFetch = null;
        this._boundUserId = null;
    }

    /**
     * Single-user-per-instance contract: the cache and pending fetch are not
     * keyed by userId. Callers must use one service instance per userId.
     * The first userId passed binds the instance; subsequent calls with a
     * different userId throw to surface invariant violations.
     */
    public async getValidToken(userId: string): Promise<string> {
        if (!userId || userId.trim().length === 0) {
            throw new Error('missing_player_id');
        }

        if (this._boundUserId === null) {
            this._boundUserId = userId;
        } else if (this._boundUserId !== userId) {
            throw new Error('GameLoopJwtTokenService: userId mismatch (single-user-per-instance contract)');
        }

        if (this._token !== null && !this._isExpiringSoon(this._token)) {
            return this._token;
        }

        if (this._pendingFetch === null) {
            this._pendingFetch = this._fetchAndCacheToken(userId);
        }

        return this._pendingFetch;
    }

    public clear(): void {
        this._token = null;
        this._pendingFetch = null;
    }

    private async _fetchAndCacheToken(userId: string): Promise<string> {
        try {
            const token = await this._fetchToken(userId);
            this._token = token;
            return token;
        } finally {
            this._pendingFetch = null;
        }
    }

    private async _fetchToken(userId: string): Promise<string> {
        const response = await fetch(this._envConfig.authTokenUrl.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                user_id: userId,
                app_host: this._envConfig.appHost
            })
        });

        if (!response.ok) {
            throw new Error(`Auth request failed: ${response.status}`);
        }

        const data = (await response.json()) as TokenResponseBody;
        if (typeof data.token !== 'string' || data.token.length === 0) {
            throw new Error('Auth response missing token');
        }

        return data.token;
    }

    private _isExpiringSoon(token: string): boolean {
        const exp = this._parseExp(token);
        if (exp === null) {
            return true;
        }

        const nowSeconds = Math.floor(Date.now() / 1000);
        return exp - nowSeconds < this._skewSeconds;
    }

    private _parseExp(token: string): number | null {
        try {
            const segments = token.split('.');
            if (segments.length !== 3) {
                return null;
            }

            const decoded = atob(this._base64UrlToBase64(segments[1]));
            const payload = JSON.parse(decoded) as JwtPayload;
            return typeof payload.exp === 'number' ? payload.exp : null;
        } catch (_error) {
            return null;
        }
    }

    private _base64UrlToBase64(input: string): string {
        const replaced = input.replace(/-/g, '+').replace(/_/g, '/');
        const paddingNeeded = (4 - (replaced.length % 4)) % 4;
        return replaced + '='.repeat(paddingNeeded);
    }
}
